import { useState } from "react";

/**
 * Custom React hook for synchronizing a state variable with localStorage.
 *
 * This hook provides a stateful value and a setter function, similar to useState,
 * but also persists the value to localStorage under the specified key.
 * It initializes the state from localStorage if available, or uses the provided initial value.
 * Updates to the value are reflected in both state and localStorage.
 *
 * @param {string} storageKey - The key under which the value is stored in localStorage.
 * @param {any} initialValue - The initial value to use if nothing is found in localStorage.
 * @returns {[any, Function]} - An array containing the current value and a setter function.
 *
 * @example
 * const [value, setValue] = useLocalStorage('myKey', 'default');
 * setValue('newValue');
 */
export const useLocalStorage = (storageKey, initialValue) => {
  
  /**
   * Initializes the state variable with the value from localStorage (if present),
   * or with the provided initialValue (and sets it in localStorage if not present).
   * Handles JSON parsing and errors gracefully.
   */
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = localStorage.getItem(storageKey);

      // If value is present in localStorage, return it; otherwise, set the initial value
      if (item !== null) {
        return JSON.parse(item);
      }

      // If value is not present, set the initial value in localStorage
      localStorage.setItem(storageKey, JSON.stringify(initialValue));
      return initialValue;
    } catch (error) {
      // If an error occurs, set the initial value in localStorage and return it
      console.error(`Error reading localStorage key "${storageKey}":`, error);
      localStorage.setItem(storageKey, JSON.stringify(initialValue));
      return initialValue;
    }
  });

  /**
   * Updates the value in both localStorage and state.
   * Accepts either a new value or a function that receives the current value and returns the new value.
   * Handles JSON serialization and errors gracefully.
   *
   * @param {any|Function} newValueOrFunction - The new value to store, or a function that returns the new value.
   */
  const updateStoredValue = (newValueOrFunction) => {
    try {
      const newValue =
        typeof newValueOrFunction === "function"
          ? newValueOrFunction(storedValue)
          : newValueOrFunction;

      localStorage.setItem(storageKey, JSON.stringify(newValue));
      setStoredValue(newValue);
    } catch (error) {
      console.error(`Error setting localStorage key "${storageKey}":`, error);
    }
  };

  return [storedValue, updateStoredValue];
};

export default useLocalStorage;
